import { useGetMySchoolsQuery, useMySchoolDistrictsQuery, useNavigationBarQuery } from "@crowdcoursing/api/graphql";
import { Button } from "@mui/material";
import { useMemo, useState } from "react";

import { useOrganizations } from "~hooks/useOrganizations";
import { Route } from "~services/router/Route";
import Dialog from "~ui/atoms/Dialog/Dialog";
import { CreateOrganizationDialog } from "~ui/organisms/Organizations/CreateOrganizationDialog";
import { AdminOrganizationsTemplate } from "~ui/templates/AdminOrganizationsTemplate";
import { DistrictsTemplate } from "~ui/templates/DistrictsTemplate/DistrictsTemplate";
import {
  type EntityManagementTabValue,
  EntityManagementTemplate,
} from "~ui/templates/EntityManagementTemplate/EntityManagementTemplate";
import { SchoolsTemplate } from "~ui/templates/SchoolsTemplate/SchoolsTemplate";

import { CreateDistrict } from "../CreateDistrict";
import { CreateSchool } from "../CreateSchool/CreateSchool";

const STORAGE_KEY = "entityManagement.selectedTab";

const getInitialTab = (): EntityManagementTabValue => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "districts" || stored === "schools" || stored === "organizations") {
    return stored;
  }
  return "districts";
};

// Hook for Districts
const useDistrictsPanelProps = () => {
  const { data, status } = useMySchoolDistrictsQuery();

  return {
    data: data?.mySchoolDistricts ?? [],
    status,
  };
};

// Hook for Schools
const useSchoolsPanelProps = () => {
  const { data, status } = useGetMySchoolsQuery();

  return {
    data: data?.mySchools ?? [],
    status,
  };
};

export const EntityManagementPage = () => {
  const [selectedTab, setSelectedTab] = useState<EntityManagementTabValue>(getInitialTab);
  const [isDistrictDialogOpen, setIsDistrictDialogOpen] = useState(false);
  const [isSchoolDialogOpen, setIsSchoolDialogOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);

  const districtsPanelProps = useDistrictsPanelProps();
  const schoolsPanelProps = useSchoolsPanelProps();
  const { organizations, isLoading: orgsLoading, error: orgsError } = useOrganizations();
  const { data: navData } = useNavigationBarQuery();
  const isSiteAdmin = navData?.me.isSiteAdmin ?? false;

  const handleTabChange = (newValue: EntityManagementTabValue) => {
    setSelectedTab(newValue);
    localStorage.setItem(STORAGE_KEY, newValue);
  };

  const tabs = useMemo(
    () => [
      {
        actionButton: (
          <Button color="primary" onClick={() => setIsDistrictDialogOpen(true)} variant="contained">
            Add District
          </Button>
        ),
        content: <DistrictsTemplate {...districtsPanelProps} />,
        label: "Districts",
        value: "districts" as const,
      },
      {
        actionButton: (
          <Button color="primary" onClick={() => setIsSchoolDialogOpen(true)} variant="contained">
            Add School
          </Button>
        ),
        content: <SchoolsTemplate {...schoolsPanelProps} />,
        label: "Schools",
        value: "schools" as const,
      },
      {
        actionButton: (
          <Button color="primary" onClick={() => setIsOrgDialogOpen(true)} variant="contained">
            Create
          </Button>
        ),
        content: (
          <AdminOrganizationsTemplate
            data={organizations}
            isSiteAdmin={isSiteAdmin}
            linkTo={(organization) => Route.organization({ organizationId: organization.id ?? "" })}
            showHeader={false}
            status={orgsError ? "error" : orgsLoading ? "pending" : "success"}
          />
        ),
        label: "Organizations",
        value: "organizations" as const,
      },
    ],
    [districtsPanelProps, schoolsPanelProps, organizations, isSiteAdmin, orgsError, orgsLoading],
  );

  const dialogs = (
    <>
      {/* District Dialog */}
      <Dialog
        actions={<CreateDistrict onClose={() => setIsDistrictDialogOpen(false)} />}
        dialogType="custom"
        onDialogClose={() => setIsDistrictDialogOpen(false)}
        open={isDistrictDialogOpen}
        title="Create New District"
      />

      {/* School Dialog */}
      <Dialog
        actions={<CreateSchool districtBubbleId="" districtId="" onClose={() => setIsSchoolDialogOpen(false)} />}
        dialogType="custom"
        onDialogClose={() => setIsSchoolDialogOpen(false)}
        open={isSchoolDialogOpen}
        title="Create New School"
      />

      {/* Organization Dialog */}
      {isOrgDialogOpen && <CreateOrganizationDialog onClose={() => setIsOrgDialogOpen(false)} />}
    </>
  );

  return (
    <EntityManagementTemplate dialogs={dialogs} onTabChange={handleTabChange} selectedTab={selectedTab} tabs={tabs} />
  );
};
